/**
 * BarbaController
 * @barba/core によるページ間のスムーズ遷移を管理する。
 *
 * 前提となる HTML マークアップ (既存):
 *   header.php ─ #container[data-barba="wrapper"]
 *   各 template ─ main[data-barba="container"][data-barba-namespace="..."]
 *
 * 責務:
 *   - フェード in/out トランジションを提供 (leave → enter)
 *   - beforeLeave で per-page な副作用 (Swiper / ScrollTrigger / body class) を掃除
 *   - after で per-page コントローラを再初期化し、Lenis / ScrollTrigger を refresh
 *
 * DeviceController / LenisController は global 扱いで再 init しない
 * (window イベントリスナや raf ループは 1回きり)。
 */
import barba from '@barba/core';
import { gsap } from 'gsap';

// ============================================================
// Barba 有効/無効フラグ
// ------------------------------------------------------------
// SPA 遷移に起因する不具合 (CF7 の残存表示など) の切り分け用に
// Barba を丸ごと無効化できるようにしている。
//   true  … Barba による SPA 遷移を有効化 (通常運用)
//   false … Barba を無効化 (通常のフルロード遷移に戻す)
//           per-page コントローラは初回ロード時に initPage() で走る
// ============================================================
const BARBA_ENABLED = true;

import { MenuController }           from './menu-controller';
import { HeaderController }         from './header-controller';
import { ScrollController }         from './scroll-controller';
import { SwiperController }         from './swiper-controller';
import { AccordionController }      from './accordion-controller';
import { ServiceTriggerController } from './service-trigger-controller';
import { SlideNavController }       from './slide-nav-controller';
import { ArchiveController }        from './archive-controller';
import { NewsHoverController }      from './news-hover-controller';
import { ScrollTriggerController }  from './scroll-trigger-controller';
import { ProductModalController }   from './product-modal-controller';
import { SplittingController }      from './splitting-controller';
import { HistoryAnimationController } from './history-animation-controller';
import { ContactFormController }    from './contact-form-controller';
import { LenisController }          from './lenis-controller';

/**
 * 固定ヘッダー分の高さ(px)を返す。
 * ハッシュ付き遷移時のスクロール位置補正に使う。
 * `.hide` 状態で transform が効いていても getBoundingClientRect().height は
 * 変化しないので、そのまま実高を使える。
 */
const getHeaderOffset = () => {
  const header = document.querySelector( '#header' );
  if ( !header ) return 0;
  return header.getBoundingClientRect().height;
};

/**
 * barba の遷移データからスクロール対象のハッシュ (先頭 # なし) を取得する。
 * 優先順位:
 *   1. next.url.hash            (barba v2 が href をパースした結果)
 *   2. trigger[href] の #以降    (barba が pushState 時にハッシュを落とすケースの保険)
 *   3. trigger[data-target]     (.scroll-trigger 用)
 *   4. window.location.hash     (フルロードや popstate 経由の保険)
 */
const resolveHash = ( { next, trigger } ) => {
  const nextHash = next?.url?.hash;
  if ( nextHash ) return nextHash;

  if ( trigger && typeof trigger.getAttribute === 'function' ) {
    const href = trigger.getAttribute( 'href' ) || '';
    const i = href.indexOf( '#' );
    if ( i >= 0 && i < href.length - 1 ) return href.slice( i + 1 );
    const dataTarget = trigger.getAttribute( 'data-target' );
    if ( dataTarget ) return dataTarget;
  }

  return window.location.hash ? window.location.hash.slice( 1 ) : '';
};

const PAGE_CONTROLLERS = [
  MenuController,
  HeaderController,
  ScrollController,
  SwiperController,
  AccordionController,
  ServiceTriggerController,
  SlideNavController,
  ArchiveController,
  NewsHoverController,
  ScrollTriggerController,
  ProductModalController,
  ContactFormController,
  // Splitting は他の DOM 操作より後で走らせて構造確定後に分割
  SplittingController,
  HistoryAnimationController,
];

export const BarbaController = {
  /**
   * 各ページで走らせる controller 群を実行する。
   * DOMContentLoaded 時と barba の beforeEnter フックの両方から呼ぶ。
   * container を明示的に渡すことで、barba 遷移中に旧/新 container が
   * 一時的に共存するタイミングでも controller が正しい方を参照できる。
   */
  initPage( container ) {
    PAGE_CONTROLLERS.forEach( ( c ) => c.init?.( container ) );
  },

  init() {
    // 初回ロード分の per-page 初期化
    this.initPage();

    // Barba 無効時はここで終了 (フルロード遷移になるので後段の barba セットアップは不要)
    if ( ! BARBA_ENABLED ) return;

    barba.init( {
      // ページ内アンカーや外部リンクは barba でハンドルしない
      prevent: ( { el } ) => {
        if ( !el ) return false;
        if ( el.hasAttribute( 'data-barba-prevent' ) ) return true;
        if ( el.target === '_blank' ) return true;
        const href = el.getAttribute( 'href' ) || '';
        if ( href.startsWith( '#' ) ) return true;
        return false;
      },

      transitions: [
        {
          name: 'fade',
          leave( { current } ) {
            // 遷移前の掃除: menu を閉じ、Swiper / ScrollTrigger を破棄する。
            // これを怠ると新ページで listener や instance が重複する。
            MenuController.close?.();
            SwiperController.reset?.();
            if ( typeof ScrollTrigger !== 'undefined' ) {
              ScrollTrigger.getAll().forEach( ( st ) => st.kill() );
            }
            // 遷移中は #header / #site-branding を常時 .show に固定。
            // front-page で .hide + body.menu-open だった状態から MenuController.close で
            // menu-open が外れると .hide が効いてヘッダーが上に引っ込むため、
            // ここで明示的に .show に固定して遷移中の意図しないスライドアップを防ぐ。
            // 新ページ用の状態は beforeEnter → HeaderController.init が再セットする。
            [ '#header', '#site-branding' ].forEach( ( sel ) => {
              const el = document.querySelector( sel );
              if ( !el ) return;
              el.classList.add( 'show' );
              el.classList.remove( 'hide' );
            } );
            return gsap.to( current.container, {
              opacity: 0,
              duration: 0.4,
              ease: 'power1.out',
              onComplete: () => {
                // ★ 旧 container をレイアウトから外す。
                //   Barba は enter 完了後まで旧 container を DOM 上に残す仕様のため、
                //   何もしないと enter フック時点で「旧 container の下に新 container」
                //   というレイアウトになり、#hash ターゲットの Y 座標が旧 container 分
                //   ズレる & Lenis の scrollHeight (=limit) も過大になる。
                //   display:none にすれば DOM 上には残しつつレイアウトから消せる。
                current.container.style.display = 'none';
              },
            } );
          },
          enter( data ) {
            const { next } = data;
            // 遷移先 URL にハッシュがある場合は、その要素へジャンプする。
            // 下層ページから front-page の #visit-us 等アンカー付きリンクで戻った際、
            // トップ固定のままだと目的セクションに飛べないための対応。
            const hash = resolveHash( data );
            const target = hash
              ? ( next.container?.querySelector( `#${ CSS.escape( hash ) }` )
                  ?? document.getElementById( hash ) )
              : null;

            if ( target ) {
              // 固定ヘッダー分のオフセット。target の top からヘッダー高さを引いた位置に着地させる。
              const headerOffset = getHeaderOffset();
              // 新 container はまだ opacity:0 だが DOM 上には居るので位置計測は可能。
              // ★ Lenis の内部 limit (this.scrollHeight - this.height) は ResizeObserver
              //    経由で更新されるが 250ms debounce なので SPA 遷移直後は前ページの
              //    (短い) 高さで clamp され、下部セクションが top 付近に丸められる。
              //    resize() を手動で叩いて limit を確定させてから scrollTo する。
              LenisController.lenis?.resize?.();
              if ( LenisController.lenis ) {
                LenisController.lenis.scrollTo( target, { immediate: true, force: true, offset: -headerOffset } );
              } else {
                const y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
                window.scrollTo( 0, y );
              }
            } else {
              // スクロール位置は Lenis 経由でトップへ (慣性を止める)
              LenisController.lenis?.scrollTo( 0, { immediate: true, force: true } );
              window.scrollTo( 0, 0 );
            }
            gsap.set( next.container, { opacity: 0 } );
            return gsap.to( next.container, {
              opacity: 1,
              duration: 0.4,
              ease: 'power1.out',
            } );
          },
        },
      ],
    } );

    // 新しい container が DOM に入った直後に per-page 再初期化。
    // enter (scrollTo(0) を含む) の前に走らせることで、
    //   - Header の scroll listener を新ページ用に貼り直してから scrollTo が発火する
    //     → 古い listener が誤検知して .show/.hide がバウンドする問題を防ぐ
    //   - front-page 以外なら isFrontPage=false → setShow(true) が確定
    barba.hooks.beforeEnter( ( { next } ) => {
      this.initPage( next?.container );
      // 下層ページ遷移時、ヘッダー各項目の href を "/#target" 形式に固定する。
      // これで下層で押下 → barba が home へ遷移 → after フックの hash scroll が
      // 該当セクションへ着地する動線が確定する。
      // front-page 遷移時は PHP 出力 (home + #target) のままにしておき、
      // scroll-trigger-controller の preventDefault でページ内スムーズスクロールに任せる。
      const isFrontPage = next?.container?.id === 'front-page';
      if ( ! isFrontPage ) {
        const home = window.location.origin + '/';
        document.querySelectorAll( '#header .scroll-trigger[data-target]' ).forEach( ( a ) => {
          const target = a.dataset.target;
          if ( ! target ) return;
          a.setAttribute( 'href', `${ home }#${ target }` );
        } );
      }
      // barba で差し替えられた <video autoplay> は自動再生されないブラウザが多いので
      // 明示的に .play() する (muted + playsinline 前提)
      next?.container?.querySelectorAll?.( 'video' ).forEach( ( v ) => {
        const p = v.play?.();
        if ( p && typeof p.catch === 'function' ) p.catch( () => {} );
      } );
    } );

    barba.hooks.after( ( data ) => {
      const { next } = data;
      if ( typeof ScrollTrigger !== 'undefined' ) {
        ScrollTrigger.refresh();
      }

      // front-page に戻ってきた際、Lenis.scrollTo(immediate:true) の scroll イベントが
      // 発火しないケースがあり、HeaderController の内部 shown フラグと DOM の
      // .show / .hide 表示が乖離する。scrollTo 完了後の位置を根拠に
      // 明示的に再評価して .hide のトグルを確定させる。
      if ( next?.container?.id === 'front-page' ) {
        // 描画確定後に評価するため 1frame 待つ
        requestAnimationFrame( () => HeaderController.refresh?.() );
      }

      // ★ #hash ターゲットへの追従再スクロール
      //   enter 時点でも scrollTo しているが、遷移直後は次ページ上部の画像 (atelier /
      //   savoir-faire / history 等) がまだロード中でセクション高さが確定しておらず、
      //   #visit-us のような下部セクションは後からロードされた画像分だけ下にズレる。
      //   → 画像ロード完了 & 数フレーム後に再スクロールして正確な位置に合わせる。
      //   ユーザーがすでに大きく手動スクロールしていたら邪魔しない。
      const hash = resolveHash( data );
      if ( !hash ) return;

      // pushState 時に hash が落ちるケースがあるので URL バーを補正しておく
      // (ブラウザ「戻る」でハッシュ付き URL に戻れるようにするため)。
      if ( window.location.hash.slice( 1 ) !== hash ) {
        const base = window.location.pathname + window.location.search;
        window.history.replaceState( window.history.state, '', `${ base }#${ hash }` );
      }
      const target = next?.container?.querySelector( `#${ CSS.escape( hash ) }` )
                  ?? document.getElementById( hash );
      if ( !target ) return;

      const scrollToTarget = () => {
        const headerOffset = getHeaderOffset();
        LenisController.lenis?.resize?.();
        if ( LenisController.lenis ) {
          LenisController.lenis.scrollTo( target, { immediate: true, force: true, offset: -headerOffset } );
        } else {
          const y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo( 0, y );
        }
        if ( typeof ScrollTrigger !== 'undefined' ) ScrollTrigger.refresh();
      };

      const currentScrollY = () => LenisController.lenis?.scroll ?? window.pageYOffset;
      // ユーザーが「まだ target 付近に居る」場合のみ再スクロールする閾値 (px)
      // これより離れていたら手動スクロールしたと見なして触らない。
      const NEAR_THRESHOLD = 400;
      const nearTarget = () => {
        const expected = target.getBoundingClientRect().top + currentScrollY() - getHeaderOffset();
        return Math.abs( expected - currentScrollY() ) < NEAR_THRESHOLD;
      };

      // 直後の rAF: レイアウトが落ち着いたタイミングで一度合わせる
      requestAnimationFrame( () => { if ( nearTarget() ) scrollToTarget(); } );

      // container 内画像のロード完了を待って再度合わせる (実質的な最終調整)
      const imgs = next.container.querySelectorAll( 'img' );
      const pending = [];
      imgs.forEach( ( img ) => {
        if ( !img.complete ) {
          pending.push( new Promise( ( res ) => {
            img.addEventListener( 'load',  res, { once: true } );
            img.addEventListener( 'error', res, { once: true } );
          } ) );
        }
      } );
      if ( pending.length ) {
        Promise.all( pending ).then( () => {
          if ( nearTarget() ) scrollToTarget();
        } );
      }
    } );
  },
};
